import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  AdminVacantesService,
  AdminVacante,
  AdminPostulante,
  AdminFiltros,
  AdminPage
} from 'src/app/services/admin-vacantes/admin-vacantes.service';

@Component({
  selector: 'app-admin-vacantes',
  templateUrl: './admin-vacantes.component.html',
  styleUrls: ['./admin-vacantes.component.scss']
})
export class AdminVacantesComponent implements OnInit, OnDestroy {

  // ── Data ──────────────────────────────────────────────────────────────────
  vacantes: AdminVacante[] = [];
  totalElements = 0;
  totalPages    = 0;
  page          = 0;
  size          = 20;
  cargando      = false;
  exportando    = false;

  // ── Filters ───────────────────────────────────────────────────────────────
  filtros: AdminFiltros = {
    buscar: '', empresa: '', fuente: '', estado: '',
    desde: '', hasta: '', sortBy: 'createdAt', sortDir: 'desc'
  };

  private filtroSubject = new Subject<void>();
  private destroy$      = new Subject<void>();

  // ── Subgrid ───────────────────────────────────────────────────────────────
  expanded    = new Set<number>();
  postulantes = new Map<number, AdminPostulante[]>();
  loadingRows = new Set<number>();

  constructor(
    private svc: AdminVacantesService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Debounce text filters, instant for selects/dates
    this.filtroSubject.pipe(debounceTime(350), takeUntil(this.destroy$))
      .subscribe(() => { this.page = 0; this.cargar(); });
    this.cargar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Load page ─────────────────────────────────────────────────────────────
  cargar(): void {
    this.cargando = true;
    this.svc.listarVacantes(this.filtros, this.page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: AdminPage) => {
          this.vacantes      = res.content;
          this.totalElements = res.totalElements;
          this.totalPages    = res.totalPages;
          this.cargando      = false;
        },
        error: () => { this.cargando = false; }
      });
  }

  // ── Filters ───────────────────────────────────────────────────────────────
  onTexto(): void { this.filtroSubject.next(); }

  onFiltro(): void { this.page = 0; this.cargar(); }

  limpiar(): void {
    this.filtros = { buscar: '', empresa: '', fuente: '', estado: '',
                     desde: '', hasta: '', sortBy: 'createdAt', sortDir: 'desc' };
    this.page = 0;
    this.cargar();
  }

  // ── Sorting ───────────────────────────────────────────────────────────────
  sort(campo: string): void {
    if (this.filtros.sortBy === campo) {
      this.filtros.sortDir = this.filtros.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.filtros.sortBy  = campo;
      this.filtros.sortDir = 'desc';
    }
    this.page = 0;
    this.cargar();
  }

  sortIcon(campo: string): string {
    if (this.filtros.sortBy !== campo) return '↕';
    return this.filtros.sortDir === 'asc' ? '↑' : '↓';
  }

  // ── Pagination ────────────────────────────────────────────────────────────
  get pageNums(): number[] {
    const total = this.totalPages;
    const cur   = this.page;
    const pages: number[] = [];
    const start = Math.max(0, cur - 2);
    const end   = Math.min(total - 1, cur + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  ir(p: number): void {
    if (p < 0 || p >= this.totalPages) return;
    this.page = p;
    this.cargar();
  }

  onSize(event: Event): void {
    this.size = parseInt((event.target as HTMLSelectElement).value, 10);
    this.page = 0;
    this.cargar();
  }

  get desde(): number { return this.page * this.size + 1; }
  get hasta(): number { return Math.min((this.page + 1) * this.size, this.totalElements); }

  // ── Subgrid ───────────────────────────────────────────────────────────────
  toggleRow(id: number): void {
    if (this.expanded.has(id)) {
      this.expanded.delete(id);
      return;
    }
    this.expanded.add(id);
    if (!this.postulantes.has(id)) {
      this.loadingRows.add(id);
      this.svc.listarPostulantes(id).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data) => {
          this.postulantes.set(id, data);
          this.loadingRows.delete(id);
        },
        error: () => {
          this.postulantes.set(id, []);
          this.loadingRows.delete(id);
        }
      });
    }
  }

  rowExpanded(id: number): boolean  { return this.expanded.has(id); }
  rowLoading(id: number): boolean   { return this.loadingRows.has(id); }
  getPostulantes(id: number): AdminPostulante[] { return this.postulantes.get(id) ?? []; }

  // ── Export ────────────────────────────────────────────────────────────────
  exportar(): void {
    this.exportando = true;
    this.svc.exportarExcel(this.filtros).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a   = document.createElement('a');
        a.href    = url;
        a.download = `vacantes-${new Date().toISOString().slice(0,10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        this.exportando = false;
      },
      error: () => { this.exportando = false; }
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  fuenteBadge(fuente: string | null): string {
    if (!fuente) return 'Manual';
    return fuente.charAt(0).toUpperCase() + fuente.slice(1).toLowerCase();
  }

  formatDate(d: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('es-MX', { day:'2-digit', month:'short', year:'numeric' });
  }

  navigate(path: string): void { this.router.navigate([path]); }

  cerrarSesion(): void {
    this.authService.cerrarSesion();
    this.router.navigate(['/acceder']);
  }
}
